import { useFormik } from 'formik';
import { noop } from 'lodash';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useTypedSelector } from '../../app/rootReducer';
import {
  createFilteredIp,
  filteredIpsSelectors,
} from '../../store/filteredIpsSlice';
import { getFilteredIpValidationSchema } from '../../utils/validationSchemas';

type CreateFormProps = {
  onCancel?: () => void;
};

export const CreateForm: FunctionComponent<CreateFormProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const types = useTypedSelector(filteredIpsSelectors.filteredIpTypes);

  const { onCancel } = props;

  const formik = useFormik({
    initialValues: {
      ip: '',
      type: types && types.full,
    },
    validationSchema: getFilteredIpValidationSchema(t),
    onSubmit: async (values) => {
      const { ip, type } = values;
      await dispatch(
        createFilteredIp({
          ip,
          type,
        })
      );

      await dispatch(onCancel);
    },
  });

  const inputRef = useRef(null);
  useEffect(() => {
    inputRef.current.focus();
  }, []);

  const isFetching = formik.isSubmitting && !formik.isValidating;
  const isSubmitButtonDisabled =
    !!formik.errors.ip || !!formik.errors.type || isFetching || !formik.dirty;

  return (
    <form onSubmit={formik.handleSubmit} className="font-ag2 gray-color-2">
      <div className="form-group row">
        <label
          htmlFor="ip"
          className="col-sm-4 col-form-label text-overflow-ellipsis"
        >
          {t('createFilteredIp.ip')}
        </label>
        <div className="col-sm-8 d-flex align-items-center flex-column">
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            id="ip"
            name="ip"
            value={formik.values.ip}
            onChange={formik.handleChange}
          />
          <small className="d-block w-100 text-danger text-right">
            {formik.errors.ip}
          </small>
        </div>
      </div>
      <div className="form-group row">
        <label htmlFor="type" className="col-sm-4 col-form-label word-wrap">
          {t('createFilteredIp.type')}
        </label>
        <div className="col-sm-8 d-flex align-items-center flex-column">
          <select
            id="type"
            name="type"
            className="form-control"
            value={formik.values.type}
            onChange={formik.handleChange}
          >
            {types &&
              Object.keys(types).map((item) => {
                return (
                  <option value={types[item]} label={types[item]} key={item} />
                );
              })}
          </select>
          <small className="d-block w-100 text-danger text-right">
            {formik.errors.type}
          </small>
        </div>
      </div>
      <div className="form-group row justify-content-end mb-1">
        <div className="mr-3">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary ml-1"
            disabled={isSubmitButtonDisabled}
          >
            {t('createFilteredIp.submitCreate')}
          </button>
        </div>
      </div>
    </form>
  );
};

CreateForm.defaultProps = {
  onCancel: noop,
};
